'use client'

import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Link, useParams } from 'react-router-dom'
import '@/styles/bauhaus.css'
import { useLanguage } from '@/i18n/LanguageProvider'
import { appLanguageToContentLocale } from '@/i18n/contentLocale'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import 'github-markdown-css/github-markdown-dark.css'
import guestData from '@/data/guests.json'

/** 文稿仅有 zh / de；UI 为 EN 时先用 de 再回退 zh */
const langMap: Record<string, string> = {
  zh: 'zh',
  en: 'de',
  de: 'de',
}

export default function GuestTalkPage() {
  const { t, language } = useLanguage()
  const params = useParams()
  const eventId = params.eventId as string
  const guestId = params.guestId as string
  const [markdownContent, setMarkdownContent] = useState<string>('')

  const guest = guestData[guestId as keyof typeof guestData]
  const langKey = appLanguageToContentLocale(language)
  const lang = langMap[langKey] || langKey

  useEffect(() => {
    const fetchContent = async () => {
      const tryLangs =
        lang === 'de' ? ['de', 'zh'] : lang === 'zh' ? ['zh', 'de'] : ['de', 'zh']
      for (const tryLang of tryLangs) {
        try {
          const response = await fetch(`/api/talk?guestId=${guestId}&lang=${tryLang}`)
          if (!response.ok) continue
          const text = await response.text()
          setMarkdownContent(text)
          return
        } catch {
          /* try next */
        }
      }
      setMarkdownContent('Document not found')
    }

    void fetchContent()
  }, [eventId, guestId, lang])

  if (!guest) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1C1C1C] to-[#252525] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bauhaus text-white mb-4">{t('bpf.events.details.not_found') || 'Not Found'}</h1>
          <Link to={`/Qiaodong/BPF/events/${eventId}`} className="text-[#B9AE9A] hover:text-white transition-colors">
            {t('bpf.events.details.back_to_event') || 'Back to Event'}
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1C1C1C] to-[#252525]">
      {/* 页面头部 */}
      <div className="pt-20 pb-8 relative bg-gradient-to-b from-[#1C1C1C] to-[#1C1C1C]">
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        <div className="absolute w-full h-[200px] bg-[radial-gradient(ellipse_at_center,_rgba(120,120,120,0.1)_0%,rgba(0,0,0,0)_70%)]"></div>
        
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          {/* 返回链接 */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-8"
          >
            <Link 
              to={`/Qiaodong/BPF/events/${eventId}`}
              className="inline-flex items-center text-[#B9AE9A]/60 hover:text-[#B9AE9A] transition-colors duration-300 text-sm font-bauhaus"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              {t('bpf.events.details.back_to_event') || '返回活动'}
            </Link>
          </motion.div>

          <div className="text-center mb-12">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-4xl md:text-5xl font-bauhaus text-white mb-6"
            >
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#B9AE9A] to-[#8C4233]">
                {guest.name}
              </span>
            </motion.h1>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="flex items-center justify-center space-x-4"
            >
              <div className="bg-[#B9AE9A]/20 backdrop-blur-sm rounded-lg px-4 py-2">
                <span className="text-[#B9AE9A] font-bauhaus text-sm">
                  {typeof guest.title === 'string' ? guest.title : guest.title[lang as keyof typeof guest.title]}
                </span>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* 主要内容 */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="mb-16"
        >
          <div className="bg-[#1C1C1C]/30 backdrop-blur-sm border border-[#B9AE9A]/10 rounded-xl overflow-hidden">
            <article className="markdown-body p-8 md:p-12" style={{ background: 'transparent', color: '#FAF9F6' }}>
              <style>{`
                .markdown-body {
                  background-color: transparent !important;
                  color: #FAF9F6 !important;
                }
                .markdown-body h1,
                .markdown-body h2,
                .markdown-body h3,
                .markdown-body h4,
                .markdown-body h5,
                .markdown-body h6 {
                  color: #FAF9F6 !important;
                  border-bottom: none !important;
                  padding-bottom: 0 !important;
                }
                .markdown-body p {
                  color: #FAF9F6 !important;
                }
                .markdown-body ul > li {
                  list-style: none !important;
                  position: relative;
                  padding-left: 1.2em;
                  color: #FAF9F6 !important;
                }
                .markdown-body ul > li::before {
                  content: '-';
                  position: absolute;
                  left: 0;
                  color: inherit;
                }
                .markdown-body a {
                  color: #B9AE9A !important;
                }
                .markdown-body a:hover {
                  color: #FAF9F6 !important;
                }
                .markdown-body code {
                  background-color: rgba(185, 174, 154, 0.1) !important;
                  color: #FAF9F6 !important;
                }
                .markdown-body pre {
                  background-color: rgba(185, 174, 154, 0.1) !important;
                  color: #FAF9F6 !important;
                }
                .markdown-body blockquote {
                  border-left-color: #B9AE9A !important;
                  color: #FAF9F6 !important;
                }
                .markdown-body table {
                  color: #FAF9F6 !important;
                }
                .markdown-body table th,
                .markdown-body table td {
                  border-color: rgba(185, 174, 154, 0.2) !important;
                }
                .markdown-body hr {
                  border-color: rgba(185, 174, 154, 0.2) !important;
                }
              `}</style>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{markdownContent}</ReactMarkdown>
            </article>
          </div>
        </motion.div>
      </div>
    </div>
  )
} 